import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Menu, Bell, Moon, Sun, ChevronRight } from 'lucide-react';
import { routes } from '../../../routes/main';
import { useTheme } from '../../contexts/ThemeContext';
import { GET_OEE_ALERTS } from '../../../apollo/oee.graphql';
const VISIBLE_SEVERITIES = new Set(['MEDIUM', 'HIGH', 'CRITICAL']);
const Header = ({ onOpenMobileNav }) => {
    const location = useLocation();
    const { isDark, toggleTheme } = useTheme();
    // live count of active medium+ alerts for the bell badge
    const { data: alertsData } = useQuery(GET_OEE_ALERTS, {
        variables: { limit: 200 },
        fetchPolicy: 'cache-first',
        pollInterval: 60000,
    });
    const activeAlerts = ((alertsData === null || alertsData === void 0 ? void 0 : alertsData.oeeAlerts) || []).filter((a) => !a.resolved && VISIBLE_SEVERITIES.has(a.severity)).length;
    const crumbs = buildCrumbs(location.pathname);
    return (_jsxs("header", { className: "sticky top-0 z-30 flex items-center gap-2 h-11 px-2 lg:px-4 bg-white/95 dark:bg-gray-950/95 backdrop-blur border-b border-gray-200 dark:border-gray-800", children: [_jsx("button", { onClick: onOpenMobileNav, "aria-label": "Open navigation", className: "lg:hidden h-8 w-8 inline-flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-white", children: _jsx(Menu, { className: "h-4 w-4" }) }), _jsx("nav", { "aria-label": "Breadcrumb", className: "flex-1 min-w-0 flex items-center gap-1 text-[12px] overflow-hidden", children: crumbs.map((c, i) => {
                    const isLast = i === crumbs.length - 1;
                    return (_jsxs("div", { className: "flex items-center gap-1 min-w-0", children: [i > 0 && (_jsx(ChevronRight, { className: "h-3 w-3 text-gray-300 dark:text-gray-700 shrink-0" })), c.href && !isLast ? (_jsx(Link, { to: c.href, className: "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white truncate", children: c.label })) : (_jsx("span", { className: `truncate ${isLast
                                    ? 'text-gray-900 dark:text-white font-medium'
                                    : 'text-gray-500 dark:text-gray-400'}`, children: c.label }))] }, i));
                }) }), _jsxs("div", { className: "flex items-center gap-0.5 shrink-0", children: [_jsxs(Link, { to: "/alert-management", "aria-label": `Alerts${activeAlerts ? `, ${activeAlerts} active` : ''}`, className: "relative h-8 w-8 inline-flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-white", children: [_jsx(Bell, { className: "h-4 w-4" }), activeAlerts > 0 && (_jsx("span", { className: "absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-red-500 text-white text-[9px] font-semibold flex items-center justify-center tabular-nums", children: activeAlerts > 99 ? '99+' : activeAlerts }))] }), _jsx("button", { onClick: toggleTheme, "aria-label": "Toggle theme", className: "h-8 w-8 inline-flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-white", children: isDark ? _jsx(Sun, { className: "h-4 w-4" }) : _jsx(Moon, { className: "h-4 w-4" }) })] })] }));
};
function buildCrumbs(pathname) {
    if (pathname === '/')
        return [{ label: 'Dashboard' }];
    // find a matching top-level route or its sub-item
    for (const route of routes) {
        if (route.path === pathname) {
            return [{ label: 'Home', href: '/' }, { label: route.label }];
        }
        if (route.subItems) {
            const sub = route.subItems.find((s) => s.path === pathname);
            if (sub) {
                return [
                    { label: 'Home', href: '/' },
                    { label: route.label, href: route.path },
                    { label: sub.label },
                ];
            }
        }
    }
    // dynamic routes — take the first segment as a fallback label
    const segs = pathname.split('/').filter(Boolean);
    return [
        { label: 'Home', href: '/' },
        ...segs.map((s) => ({
            label: s.replace(/[-_]/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
        })),
    ];
}
export default Header;
